import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { Plus, Trash2, Pencil, Save, X, FileVideo, FileText, BookOpen } from "lucide-react";

const LESSON_TYPES = [
  { value: "video", label: "Video", Icon: FileVideo },
  { value: "doc", label: "Doc / Article", Icon: FileText },
  { value: "lab", label: "Hands-on Lab", Icon: BookOpen },
];

export default function AdminLMS() {
  const [labs, setLabs] = useState([]);
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState("");
  const [newLab, setNewLab] = useState({ code: "", name: "", tagline: "", description: "", color: "#006CFF", category: "", skills: "" });
  const [newMod, setNewMod] = useState({ title: "", description: "", duration_hours: 4, order: 1 });
  const [editingMod, setEditingMod] = useState(null);
  const [editingLsn, setEditingLsn] = useState(null);
  const [newLsnFor, setNewLsnFor] = useState(null); // module_id being added to

  const loadLabs = async () => {
    const r = await api.get("/lms/labs"); setLabs(r.data);
    const s = await api.get("/admin/lms/stats"); setStats(s.data);
  };
  const loadDetail = async (lid) => {
    const r = await api.get(`/lms/labs/${lid}`); setDetail(r.data);
  };
  useEffect(() => { loadLabs(); }, []);
  useEffect(() => { if (selected) loadDetail(selected); else setDetail(null); }, [selected]);

  const createLab = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      const payload = { ...newLab, skills: newLab.skills.split(",").map(s=>s.trim()).filter(Boolean) };
      await api.post("/admin/lms/labs", payload);
      setNewLab({ code: "", name: "", tagline: "", description: "", color: "#006CFF", category: "", skills: "" });
      loadLabs();
    } catch (e) { setMsg(formatError(e)); }
  };

  const delLab = async (id) => {
    if (!window.confirm("Delete this lab and all its content?")) return;
    await api.delete(`/admin/lms/labs/${id}`);
    if (selected === id) setSelected(null);
    loadLabs();
  };

  const addModule = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/admin/lms/modules", { lab_id: selected, ...newMod });
      setNewMod({ title: "", description: "", duration_hours: 4, order: (detail?.modules?.length || 0) + 1 });
      loadDetail(selected);
    } catch (e) { setMsg(formatError(e)); }
  };
  const saveModule = async (m) => {
    try {
      await api.put(`/admin/lms/modules/${m.id}`, {
        lab_id: m.lab_id, title: m.title, description: m.description || "",
        duration_hours: Number(m.duration_hours) || 0, order: Number(m.order) || 1,
      });
      setEditingMod(null);
      loadDetail(selected);
    } catch (e) { setMsg(formatError(e)); }
  };
  const delModule = async (mid) => {
    if (!window.confirm("Delete this module and all its lessons?")) return;
    await api.delete(`/admin/lms/modules/${mid}`);
    loadDetail(selected);
  };

  const saveLesson = async (l) => {
    try {
      if (l.id) {
        await api.put(`/admin/lms/lessons/${l.id}`, {
          module_id: l.module_id, title: l.title, type: l.type,
          duration_min: Number(l.duration_min) || 5,
          content: l.content || "", order: Number(l.order) || 1,
        });
      } else {
        await api.post(`/admin/lms/lessons`, {
          module_id: l.module_id, title: l.title, type: l.type,
          duration_min: Number(l.duration_min) || 5,
          content: l.content || "", order: Number(l.order) || 1,
        });
      }
      setEditingLsn(null); setNewLsnFor(null);
      loadDetail(selected);
    } catch (e) { setMsg(formatError(e)); }
  };
  const delLesson = async (lid) => {
    if (!window.confirm("Delete this lesson?")) return;
    await api.delete(`/admin/lms/lessons/${lid}`);
    loadDetail(selected);
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-6 gap-3">
        {stats && [
          ["Labs", stats.labs], ["Modules", stats.modules], ["Lessons", stats.lessons],
          ["Enrollments", stats.enrollments], ["Completions", stats.completions], ["Certificates", stats.certificates],
        ].map(([k, v]) => (
          <div key={k} className="card card-pad" data-testid={`lms-stat-${k.toLowerCase()}`}>
            <span className="eyebrow">{k}</span>
            <div className="font-display font-black text-3xl mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <form onSubmit={createLab} className="card card-pad md:col-span-1 self-start" data-testid="create-lab-form">
          <h3 className="font-display font-bold text-xl">New CoE Lab</h3>
          <input className="input mt-3" placeholder="Code (e.g. SMPL)" required value={newLab.code} onChange={e=>setNewLab({...newLab,code:e.target.value.toUpperCase()})} data-testid="lab-code" />
          <input className="input mt-3" placeholder="Name" required value={newLab.name} onChange={e=>setNewLab({...newLab,name:e.target.value})} data-testid="lab-name-input" />
          <input className="input mt-3" placeholder="Tagline" value={newLab.tagline} onChange={e=>setNewLab({...newLab,tagline:e.target.value})} />
          <textarea className="input mt-3 min-h-[80px]" placeholder="Description" value={newLab.description} onChange={e=>setNewLab({...newLab,description:e.target.value})} />
          <input className="input mt-3" placeholder="Category" value={newLab.category} onChange={e=>setNewLab({...newLab,category:e.target.value})} />
          <input className="input mt-3" placeholder="Skills (comma)" value={newLab.skills} onChange={e=>setNewLab({...newLab,skills:e.target.value})} />
          <input className="input mt-3" type="color" value={newLab.color} onChange={e=>setNewLab({...newLab,color:e.target.value})} />
          <button className="btn-primary w-full justify-center mt-4" data-testid="lab-create-btn"><Plus size={14}/> Create lab</button>
          <div className="text-xs text-red-600 mt-2" data-testid="admin-lms-msg">{msg}</div>
        </form>

        <div className="md:col-span-2 space-y-3">
          {labs.map(l => (
            <div key={l.id} className={`card card-pad cursor-pointer transition ${selected === l.id ? "border-signal" : ""}`}
              onClick={() => setSelected(l.id === selected ? null : l.id)} data-testid={`admin-lab-${l.code}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-10 rounded-sm" style={{ background: l.color }} />
                  <div>
                    <div className="font-mono text-xs">{l.code} · {l.category} {l.sc_domain && <span className="text-signal">· {l.sc_domain}</span>}</div>
                    <div className="font-display font-bold">{l.name}</div>
                    <div className="text-xs text-muted">{l.module_count} modules · {l.enrolled_count} enrolled</div>
                  </div>
                </div>
                <button onClick={e=>{e.stopPropagation(); delLab(l.id);}} className="btn-ghost" data-testid={`del-lab-${l.code}`}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>

              {selected === l.id && detail && (
                <div className="mt-5 pt-4 border-t border-rule space-y-3" onClick={e=>e.stopPropagation()}>
                  <h4 className="eyebrow">Modules &amp; Lessons</h4>
                  {detail.modules.map((m, i) => (
                    <div key={m.id} className="p-3 border border-rule rounded-sm bg-paper space-y-2" data-testid={`admin-mod-${m.id}`}>
                      {editingMod?.id === m.id ? (
                        <div className="grid grid-cols-3 gap-2">
                          <input className="input col-span-2" value={editingMod.title} onChange={e=>setEditingMod({...editingMod, title:e.target.value})} data-testid={`mod-edit-title-${m.id}`}/>
                          <input className="input" type="number" value={editingMod.duration_hours} onChange={e=>setEditingMod({...editingMod, duration_hours:e.target.value})}/>
                          <textarea className="input col-span-3" placeholder="Description" value={editingMod.description || ""} onChange={e=>setEditingMod({...editingMod, description:e.target.value})}/>
                          <div className="col-span-3 flex justify-end gap-2">
                            <button className="btn-ghost text-xs" onClick={()=>setEditingMod(null)} data-testid={`mod-edit-cancel-${m.id}`}><X size={12}/> Cancel</button>
                            <button className="btn-primary text-xs" onClick={()=>saveModule(editingMod)} data-testid={`mod-edit-save-${m.id}`}><Save size={12}/> Save</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold">{i+1}. {m.title}</div>
                            <div className="text-xs text-muted">{m.lessons.length} lessons · {m.duration_hours}h{m.assessment ? ` · Quiz (${m.assessment.question_count} Qs)` : ""}</div>
                          </div>
                          <button onClick={()=>setEditingMod({...m})} className="btn-ghost" data-testid={`edit-mod-${m.id}`}><Pencil size={12}/></button>
                          <button onClick={()=>delModule(m.id)} className="btn-ghost" data-testid={`del-mod-${m.id}`}><Trash2 size={12}/></button>
                        </div>
                      )}
                      {/* Lessons inline */}
                      <div className="pl-4 space-y-1.5">
                        {(m.lessons || []).map((ls, j) => (
                          <LessonRow key={ls.id} lesson={ls} idx={j} editing={editingLsn?.id === ls.id} setEditing={setEditingLsn} onSave={saveLesson} onDelete={delLesson}/>
                        ))}
                        {newLsnFor === m.id ? (
                          <LessonRow lesson={{ module_id: m.id, title: "", type: "video", duration_min: 5, content: "", order: (m.lessons?.length || 0)+1 }}
                            isNew editing setEditing={setEditingLsn} onSave={(l)=>saveLesson(l)} onCancel={()=>setNewLsnFor(null)} />
                        ) : (
                          <button onClick={()=>setNewLsnFor(m.id)} className="text-xs text-signal font-bold mt-1 hover:underline" data-testid={`add-lesson-${m.id}`}>
                            <Plus size={11} className="inline -mt-0.5"/> Add lesson
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <form onSubmit={addModule} className="grid grid-cols-2 gap-2 mt-3" data-testid="add-mod-form">
                    <input className="input" placeholder="Module title" required value={newMod.title} onChange={e=>setNewMod({...newMod,title:e.target.value})} data-testid="mod-title" />
                    <input className="input" placeholder="Duration (h)" type="number" value={newMod.duration_hours} onChange={e=>setNewMod({...newMod,duration_hours:Number(e.target.value)})} />
                    <textarea className="input col-span-2" placeholder="Description" value={newMod.description} onChange={e=>setNewMod({...newMod,description:e.target.value})} />
                    <button className="btn-secondary col-span-2 justify-center" data-testid="add-mod-btn"><Plus size={14}/> Add module</button>
                  </form>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LessonRow({ lesson, idx, editing, isNew, setEditing, onSave, onDelete, onCancel }) {
  const [draft, setDraft] = useState(lesson);
  React.useEffect(() => { setDraft(lesson); }, [lesson]);
  const TypeIcon = (LESSON_TYPES.find(t => t.value === (editing ? draft.type : lesson.type)) || LESSON_TYPES[0]).Icon;
  if (editing) {
    return (
      <div className="grid grid-cols-12 gap-1.5 text-xs bg-paper2 p-2 rounded-sm" data-testid={`lesson-edit-${lesson.id || "new"}`}>
        <input className="input col-span-5" placeholder="Lesson title" value={draft.title} onChange={e=>setDraft({...draft,title:e.target.value})} data-testid={`lesson-title-${lesson.id || "new"}`}/>
        <select className="input col-span-2" value={draft.type} onChange={e=>setDraft({...draft,type:e.target.value})}>
          {LESSON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input className="input col-span-2" type="number" placeholder="min" value={draft.duration_min} onChange={e=>setDraft({...draft,duration_min:e.target.value})}/>
        <input className="input col-span-3" placeholder="Content URL / markdown" value={draft.content || ""} onChange={e=>setDraft({...draft,content:e.target.value})}/>
        <div className="col-span-12 flex justify-end gap-2">
          <button onClick={() => { isNew ? onCancel() : setEditing(null); }} className="btn-ghost text-xs"><X size={11}/> Cancel</button>
          <button onClick={() => onSave(draft)} className="btn-primary text-xs" data-testid={`lesson-save-${lesson.id || "new"}`}><Save size={11}/> Save</button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs">
      <TypeIcon size={12} className="text-signal shrink-0"/>
      <span className="flex-1 min-w-0 truncate"><span className="font-mono text-muted">{idx+1}.</span> {lesson.title}</span>
      <span className="text-muted">{lesson.duration_min}m</span>
      <button className="btn-ghost text-xs" onClick={()=>setEditing({...lesson})} data-testid={`edit-lesson-${lesson.id}`}><Pencil size={10}/></button>
      <button className="btn-ghost text-xs" onClick={()=>onDelete(lesson.id)} data-testid={`del-lesson-${lesson.id}`}><Trash2 size={10}/></button>
    </div>
  );
}
